import React, { useState, useMemo } from 'react';
import useJobs from '../../../features/jobs/hooks/useJobs';
import useCategories from '../../../features/jobs/hooks/useCategories';
import useTowns from '../../../features/jobs/hooks/useTowns';
import useAppliedJobs from '../../../features/jobs/hooks/useAppliedJobs';
import useSkillsByCategory from '../../../features/jobs/hooks/useSkillsByCategory';
import JobCard from '../components/JobCard';
import { saveJob, unsaveJob } from '../../../services/api';
import CardIcon from '../../../shared/components/icons/CardIcon';

function FindJobs() {
  const { jobs, loading: jobsLoading, error: jobsError } = useJobs();
  const { categories } = useCategories();
  const { towns } = useTowns();
  const { jobs: appliedJobs } = useAppliedJobs();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [activeFilter, setActiveFilter] = useState('recommended');
  const [searchTerm, setSearchTerm] = useState('');
  const [townSearch, setTownSearch] = useState('');

  const { skills, loading: skillsLoading, error: skillsError, setCategorySlug } = useSkillsByCategory();
  const selectedCategoryName = useMemo(
    () => categories.find((category) => (category.slug || category.id) === selectedCategory)?.name || '',
    [categories, selectedCategory],
  );

  const filteredTowns = useMemo(
    () => towns.filter((town) => String(town.name || town).toLowerCase().includes(townSearch.toLowerCase())),
    [towns, townSearch],
  );

  const appliedJobIds = useMemo(() => appliedJobs.map((job) => job.id), [appliedJobs]);

  const filteredJobs = useMemo(() => {
    let result = jobs.filter((job) => {
      if (appliedJobIds.includes(job.id) || job.hasApplied) {
        return false;
      }

      const jobCategory = job.skill?.category || {};
      const categoryValues = [jobCategory.id, jobCategory.slug, jobCategory.name, job.categoryId, job.category]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      const matchesCategory = !selectedCategory
        || categoryValues.includes(selectedCategory.toLowerCase())
        || categoryValues.includes(selectedCategoryName.toLowerCase());

      const jobSkill = job.skill || {};
      const matchesSkill = !selectedSkill || [jobSkill.id, jobSkill.name, job.skillId]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase() === selectedSkill.toLowerCase());

      const matchesTown = !selectedTown || job.city === selectedTown;

      const matchesSearch =
        !searchTerm ||
        (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.description || '').toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSkill && matchesTown && matchesSearch;
    });

    if (activeFilter === 'urgent') {
      result = result.filter((job) => job.urgency === 'urgent');
    } else if (activeFilter === 'highpay') {
      result = result.sort((a, b) => b.budget - a.budget);
    }

    return result;
  }, [jobs, selectedCategory, selectedCategoryName, selectedSkill, selectedTown, searchTerm, activeFilter, appliedJobIds]);

  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setCategorySlug(categoryValue);
    setSelectedSkill('');
  };

  const handleTownChange = (townName) => {
    setSelectedTown(townName);
    setTownSearch(townName);
  };

  const handleSave = async (jobId, nextSaved) => {
    await (nextSaved ? saveJob(jobId) : unsaveJob(jobId));
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
        <h1 className="flex items-center gap-3 text-3xl font-semibold text-navy"><CardIcon name="search" className="h-7 w-7" />Find Jobs</h1>
        <p className="mt-2 text-sm text-slate-600">
          Browse available opportunities and find the perfect match for your skills.
        </p>
      </div>

      <div className="space-y-4 rounded-3xl bg-white p-6 shadow-sm">
        <div>
          <label htmlFor="search" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <CardIcon name="search" />Search jobs
          </label>
          <input
            id="search"
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-navy"
          />
        </div>

        <div>
          <label htmlFor="category" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><CardIcon name="category" />Category</label>
          <select
            id="category"
            value={selectedCategory}
            onChange={(event) => handleCategoryChange(event.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-navy"
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.slug || category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {selectedCategory && (
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('');
                setSelectedSkill('');
                setCategorySlug('');
              }}
              className="mt-2 text-xs font-semibold text-orange-600"
            >
              Clear category
            </button>
          )}
        </div>

        {selectedCategory && (
          <div>
            <label htmlFor="skill" className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <CardIcon name="check" />Skill
            </label>
            <select
              id="skill"
              value={selectedSkill}
              onChange={(event) => setSelectedSkill(event.target.value)}
              disabled={skillsLoading || !selectedCategory}
              className="w-full rounded-lg border border-slate-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-navy disabled:bg-slate-50 disabled:text-slate-500"
            >
              <option value="">All Skills in {selectedCategoryName || 'selected category'}</option>
              {skills.map((skill) => (
                <option key={skill.id} value={skill.id}>
                  {skill.name}
                </option>
              ))}
            </select>
            {skillsError && <p className="mt-2 text-xs text-rose-600">Unable to load skills for this category.</p>}
          </div>
        )}

        <div>
          <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><CardIcon name="location" />Town/City</label>
          <div className="relative">
            <input
              type="text"
              value={selectedTown || townSearch}
              onChange={(event) => {
                setSelectedTown('');
                setTownSearch(event.target.value);
              }}
              placeholder="Search town or city"
              className="w-full rounded-lg border border-slate-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-navy"
            />
            {(townSearch || selectedTown) && !selectedTown && (
              <div className="absolute z-10 mt-2 max-h-48 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-lg">
                {filteredTowns.length > 0 ? (
                  filteredTowns.map((town) => {
                    const townName = town.name || town;
                    return (
                      <button
                        key={town.id || townName}
                        type="button"
                        onClick={() => handleTownChange(townName)}
                        className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-orange-50 hover:text-slate-900"
                      >
                        {townName}
                      </button>
                    );
                  })
                ) : (
                  <p className="px-3 py-2 text-sm text-slate-500">No matching towns</p>
                )}
              </div>
            )}
          </div>
          {selectedTown && (
            <button
              type="button"
              onClick={() => {
                setSelectedTown('');
                setTownSearch('');
              }}
              className="mt-2 text-xs font-semibold text-orange-600"
            >
              Clear town
            </button>
          )}
        </div>

        <div>
          <p className="mb-3 block text-sm font-semibold text-slate-700">Quick Filters</p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Recommended', value: 'recommended', icon: 'star' },
              { label: 'Urgent Only', value: 'urgent', icon: 'flame' },
              { label: 'Highest Pay', value: 'highpay', icon: 'money' },
            ].map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setActiveFilter(filter.value)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeFilter === filter.value ? 'bg-navy text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className="inline-flex items-center gap-1.5"><CardIcon name={filter.icon} className="h-4 w-4" />{filter.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="text-sm text-slate-600">
        Found {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
      </div>

      <div className="space-y-4">
        {jobsLoading && (
          <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading jobs...</div>
        )}
        {jobsError && (
          <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">Error: {jobsError}</div>
        )}
        {!jobsLoading && filteredJobs.length === 0 && (
          <div className="rounded-3xl bg-white p-8 text-center text-slate-500 shadow-sm">
            <p>No jobs found matching your filters.</p>
            <p className="mt-2 text-sm">Try adjusting your search criteria.</p>
          </div>
        )}
        {!jobsLoading && filteredJobs.map((job) => <JobCard key={job.id} job={job} onSave={handleSave} />)}
      </div>
    </div>
  );
}

export default FindJobs;
